// Question 1: did customers confirm the problem? Deterministic. Synthesised
// needs ranked by how many customers raised them, each tagged with the
// capability that addresses it or "Not addressed".

import { MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { NODE_LABEL } from '@/lib/node-visuals';
import { formatCustomerCount } from '@/lib/alignment-groups';
import type { AlignmentNeed } from '@/types/api';

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

export function NeedsSection({ needs }: { needs: AlignmentNeed[] }) {
  const addressed = needs.filter((n) => n.addressedBy.length > 0).length;
  const headline =
    addressed === needs.length
      ? `Customers raised ${plural(needs.length, 'need')}, and your plan addresses every one.`
      : `Customers raised ${plural(needs.length, 'need')}. Your plan addresses ${addressed} of them.`;

  return (
    <section className="rounded-lg border bg-card p-5 space-y-3">
      <h2 className="font-semibold flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-muted-foreground">1.</span>
        Did customers confirm the problem?
      </h2>
      {needs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No customer needs yet. Review your dialogs in step 5 to pull them together.
        </p>
      ) : (
        <>
          <p className="text-sm">{headline}</p>
          <ul className="space-y-2">
            {needs.map((n) => {
              const count = formatCustomerCount(n.respondentCount, n.respondentTotal);
              return (
                <li key={n.nodeId} className="rounded-md border p-3 text-sm flex items-start justify-between gap-4">
                  {/* Chip and title flow as inline text so a long title wraps after the chip. */}
                  <p className="min-w-0 flex-1 leading-snug">
                    <Badge tone={n.nodeType} className="mr-2 align-middle">
                      {NODE_LABEL[n.nodeType] ?? n.nodeType}
                    </Badge>
                    <span className="align-middle">{n.title || '(untitled)'}</span>
                    {count && <span className="ml-2 text-xs text-muted-foreground align-middle">{count}</span>}
                  </p>
                  <div className="shrink-0 max-w-[45%] text-right text-xs text-muted-foreground">
                    {n.addressedBy.length === 0 ? (
                      <Badge tone="warning">Not addressed</Badge>
                    ) : (
                      <>
                        Addressed by: {n.addressedBy[0].title || '(untitled)'}
                        {n.addressedBy.length > 1 ? ` (+${n.addressedBy.length - 1} more)` : ''}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
